/**
 * Intake-form autocomplete wiring from the managed research dictionaries
 * (E07-F03-T04, web side).
 *
 * 1:1 port of the legacy prototype `research.proto.js` intake autocomplete block
 * (../EER app/prototype/assets/research.proto.js L331-390): the three
 * `<datalist>`s (`dict-industries` / `dict-subcat` / `dict-geo`) sourced from
 * `MOCK.dictionaries`, the Industry `<input list="dict-industries">`, and the
 * chip-inputs for Sub-categories / Locations whose entry `<input>` carries the
 * matching `list=`.
 *
 * In v2 the suggestion source is the LIVE `research_get_dictionaries` snapshot
 * (researchDictionariesApi.ts → the shared `apiClient`), the SAME query the
 * Settings Dictionaries card writes — so a term added in Settings appears in
 * these Intake suggestions (T04 acceptance #3) once the cache re-renders.
 *
 * Exported pieces (the E06 Intake form composes these into its Targeting section;
 * this file owns only the dictionary-backed inputs, no other form state):
 *   - <DictionaryDatalists/>     — the three `<datalist>`s (render ONCE per form);
 *   - <IndustryAutocompleteInput/> — single-value Industry input (list=dict-industries);
 *   - <DictionaryChipInput/>     — chip field with list= autocomplete (subcat / geo).
 *
 * Autocomplete never blocks manual input: a value typed that is NOT in the
 * dictionary still commits as a chip / stays in the input (T04 acceptance #4).
 * Pure rules: ../admin/researchDictionariesModel.ts.
 */
import { useState, type KeyboardEvent, type JSX } from 'react';
import { DICT_COLUMNS, type DictKey } from '@eer/shared';
import { useResearchDictionaries } from '../admin/researchDictionariesApi.js';
import {
  chipToAdd,
  datalistIdForKey,
  isChipCommitKey,
  valuesForKey,
} from '../admin/researchDictionariesModel.js';

/**
 * The three `<datalist>`s feeding the Intake autocomplete, sourced from the live
 * dictionaries snapshot. Render this ONCE inside the Intake form; the inputs
 * below reference the lists by id. While the snapshot loads the lists are empty
 * (typing still works — autocomplete just has no suggestions yet).
 */
export function DictionaryDatalists(): JSX.Element {
  const dictionariesQuery = useResearchDictionaries();
  const snapshot = dictionariesQuery.data;

  return (
    <>
      {DICT_COLUMNS.map((col) => (
        <datalist key={col.key} id={col.datalistId} data-testid={`datalist-${col.key}`}>
          {valuesForKey(snapshot, col.key).map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
      ))}
    </>
  );
}

/**
 * Single-value Industry input wired to the `dict-industries` datalist
 * (prototype `<input list="dict-industries">`). Free typing of an off-dictionary
 * value is allowed — the datalist is a suggestion list, not a constraint.
 */
export function IndustryAutocompleteInput(props: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  testId?: string;
}): JSX.Element {
  const { value, onChange, placeholder = 'Start typing…', testId = 'intake-industry' } = props;
  return (
    <input
      type="text"
      list={datalistIdForKey('industry')}
      className="w-full border rounded p-2 text-sm focus:border-[#D4AF37] outline-none"
      placeholder={placeholder}
      aria-label="Industry"
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/**
 * Chip field with `<datalist>` autocomplete for the multi-value Intake fields
 * (Sub-categories → `dict-subcat`, Locations → `dict-geo`). Parity with the
 * prototype `chips-subcat` / `chips-geo` containers: chips wrap above the entry
 * input; Enter commits a trimmed, non-duplicate value; clicking the container
 * focuses the input. A value picked from the datalist OR typed freely both commit
 * the same way (T04 acceptance #4).
 */
export function DictionaryChipInput(props: {
  dictKey: Extract<DictKey, 'sub_category' | 'location'>;
  value: readonly string[];
  onChange: (chips: string[]) => void;
  placeholder?: string;
  testId?: string;
}): JSX.Element {
  const { dictKey, value, onChange, placeholder, testId = `intake-${dictKey}` } = props;
  const [draft, setDraft] = useState('');
  const listId = datalistIdForKey(dictKey);

  function commit(): void {
    const next = chipToAdd(draft, value);
    setDraft('');
    if (next === null) return;
    onChange([...value, next]);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>): void {
    if (!isChipCommitKey(e)) return;
    e.preventDefault();
    commit();
  }

  function removeChip(chip: string): void {
    onChange(value.filter((c) => c !== chip));
  }

  return (
    <div
      className="flex flex-wrap gap-1 p-1.5 border rounded min-h-[38px] bg-white cursor-text"
      data-testid={`${testId}-chips`}
      onClick={(e) => {
        (e.currentTarget.querySelector('input') as HTMLInputElement | null)?.focus();
      }}
    >
      {value.map((chip) => (
        <span
          key={chip}
          className="bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
          data-testid="intake-chip"
          data-chip-value={chip}
        >
          {chip}
          <button
            type="button"
            className="text-blue-500 hover:text-red-500 font-bold leading-none"
            aria-label={`Remove ${chip}`}
            title="Remove"
            onClick={(e) => {
              e.stopPropagation();
              removeChip(chip);
            }}
          >
            {'×'}
          </button>
        </span>
      ))}
      <input
        type="text"
        list={listId}
        className="border-0 outline-none text-xs flex-1 min-w-[100px] py-0.5"
        placeholder={placeholder}
        aria-label={dictKey === 'sub_category' ? 'Add sub-category' : 'Add region / country'}
        data-testid={`${testId}-input`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={commit}
      />
    </div>
  );
}

export default DictionaryDatalists;
